import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Location, WorkLocation } from '../types';

interface LocationMapProps {
  currentLocation: Location | null;
  workLocation: Location;
  allWorkLocations?: WorkLocation[];
  nearestLocation?: WorkLocation | null;
  className?: string;
}

const LocationMap: React.FC<LocationMapProps> = ({ 
  currentLocation, 
  workLocation, 
  allWorkLocations = [],
  nearestLocation,
  className = "h-64 w-full rounded-lg" 
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || !currentLocation) return;

    // Clean up previous map instance
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    // Create map instance
    const map = L.map(mapRef.current).setView(
      [currentLocation.latitude, currentLocation.longitude], 
      14
    );

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Create custom markers
    const currentMarkerIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="width: 20px; height: 20px; background-color: #ef4444; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    const workMarkerIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="width: 20px; height: 20px; background-color: #3b82f6; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });

    const nearestMarkerIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="width: 24px; height: 24px; background-color: #10b981; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    const otherWorkMarkerIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="width: 18px; height: 18px; background-color: #6b7280; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    });

    // Add current location marker
    const currentMarker = L.marker([currentLocation.latitude, currentLocation.longitude], {
      icon: currentMarkerIcon
    }).addTo(map);

    currentMarker.bindPopup(`
      <div style="text-align: center;">
        <strong>Sizin Konumunuz</strong><br>
        ${currentLocation.address || 'Mevcut konum'}
      </div>
    `);

    // Collect all markers for bounds calculation
    const allMarkers = [currentMarker];

    // Add all work location markers
    const locationsToShow = allWorkLocations.length > 0 ? allWorkLocations : [workLocation];
    
    locationsToShow.forEach(location => {
      // Skip invalid locations
      if (typeof location.latitude !== 'number' || typeof location.longitude !== 'number' ||
          isNaN(location.latitude) || isNaN(location.longitude)) {
        return;
      }

      const isNearest = nearestLocation && 'id' in location && location.id === nearestLocation.id;
      const icon = isNearest ? nearestMarkerIcon : 
                   (allWorkLocations.length > 0 ? otherWorkMarkerIcon : workMarkerIcon);

      const marker = L.marker([location.latitude, location.longitude], {
        icon: icon
      }).addTo(map);

      const popupContent = `
        <div style="text-align: center;">
          <strong>${isNearest ? '🎯 En Yakın İş Yeri' : '🏢 İş Yeri Konumu'}</strong><br>
          <strong>${location.name || 'İş Yeri'}</strong><br>
          ${location.address || 'Adres bilgisi yok'}
        </div>
      `;

      marker.bindPopup(popupContent);
      allMarkers.push(marker);
    });

    // Fit map to show all markers
    if (allMarkers.length > 1) {
      const group = new L.FeatureGroup(allMarkers);
      map.fitBounds(group.getBounds().pad(0.1));
    }

    // Store map instance for cleanup
    mapInstanceRef.current = map;

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [currentLocation, workLocation, allWorkLocations, nearestLocation]);

  if (!currentLocation) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Harita yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
          <span>Sizin Konumunuz</span>
        </div>
        {allWorkLocations.length > 1 ? (
          <>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span>En Yakın İş Yeri</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-500 rounded-full mr-2"></div>
              <span>Diğer İş Yerleri</span>
            </div>
          </>
        ) : (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span>İş Yeri Konumu</span>
          </div>
        )}
      </div>
      <div ref={mapRef} className={className} style={{ minHeight: '256px' }}></div>
      <div className="text-xs text-gray-500 text-center">
        💡 Detaylar için işaretlere tıklayın, haritayı hareket ettirmek için sürükleyin, yakınlaştırmak için kaydırın
        {allWorkLocations.length > 1 && (
          <>
            <br />
            🎯 Yeşil işaret size en yakın iş yerini gösterir
          </>
        )}
      </div>
    </div>
  );
};

export default LocationMap; 